import { match, P } from 'ts-pattern';
import {
  getReadonlyListItemCurrency,
  getReadonlyListItemNode,
  getReadonlyListItemNumber,
  getReadonlyListItemSensitiveString,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import { PlainIconButton } from '@interxfi/design-system/components/button';
import { maskEIN, maskITIN, maskSSN } from '@interxfi/design-system/components/format/sensitive-value';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import InfoIcon from '@interxfi/design-system/icons/react/icon-info';
import { buildTooltipInteraction } from '@interxfi/design-system/util';
import { useTranslation } from '@interxfi/translation';
import {
  type IxcRegistrationV1AccountFundingAmountLimits,
  type IxcRegistrationV1Address,
  type IxcRegistrationV1AddressUsAddress,
  type IxcRegistrationV1ClearingAccountControls,
  type IxcRegistrationV1ClearingAccountControlsAccountNumbers,
  type IxcRegistrationV1ClearingAccountControlsAccountNumbersSettings,
  type IxcRegistrationV1ClearingAccountControlsBatchOrder,
  type IxcRegistrationV1ClearingAccountControlsFunding,
  type IxcRegistrationV1ClearingAccountControlsOrder,
  type IxcRegistrationV1ClearingAccountControlsSweeps,
  type IxcRegistrationV1ClearingControls,
  type IxcRegistrationV1ClearingControlsSettings,
  IxcRegistrationV1ControlsMarginType,
  type IxcRegistrationV1IndividualTaxIdentifier,
  type IxcRegistrationV1Name,
  type IxcRegistrationV1OrganizationTaxIdentifier,
  type IxcRegistrationV1PlatformControls,
  type IxcRegistrationV1PlatformControlsCredit,
  type IxcRegistrationV1PlatformControlsCreditSettings,
  type IxcRegistrationV1ProfileSummaryData,
  type IxcRegistrationV1ProgramControls,
  type IxcRegistrationV1ProgramControlsAccountNumbers,
  type IxcRegistrationV1ProgramControlsAccountNumbersSettings,
  type IxcRegistrationV1ProgramControlsBatchOrder,
  type IxcRegistrationV1ProgramControlsBatchOrderSettings,
  type IxcRegistrationV1ProgramControlsBatchOrderSettingsUsEquities,
  type IxcRegistrationV1ProgramControlsFunding,
  type IxcRegistrationV1ProgramControlsFundingLimits,
  type IxcRegistrationV1ProgramControlsFundingLimitsAch,
  type IxcRegistrationV1ProgramControlsFundingLimitsSameDayAch,
  type IxcRegistrationV1ProgramControlsFundingLimitsWire,
  type IxcRegistrationV1ProgramControlsFundingSettings,
  type IxcRegistrationV1ProgramControlsFundingSettingsAch,
  type IxcRegistrationV1ProgramControlsFundingSettingsSameDayAch,
  type IxcRegistrationV1ProgramControlsFundingSettingsWire,
  type IxcRegistrationV1ProgramControlsInstantDeposit,
  type IxcRegistrationV1ProgramControlsInstantDepositLimits,
  type IxcRegistrationV1ProgramControlsInstantDepositSettings,
  type IxcRegistrationV1ProgramControlsOrder,
  type IxcRegistrationV1ProgramControlsOrderSettings,
  type IxcRegistrationV1ProgramControlsOrderSettingsUsEquities,
  type IxcRegistrationV1ProgramControlsRegistration,
  type IxcRegistrationV1ProgramControlsRegistrationSettings,
  type IxcRegistrationV1ProgramControlsSweeps,
  type IxcRegistrationV1ProgramControlsSweepsSettings,
  type IxcRegistrationV1RegistrationAccountType,
  type IxcRegistrationV1UsState,
} from '@interxfi/types/generated/api';
import { getOneOfType } from '@interxfi/types/helpers/one-of';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { getHasAllUsStatesAndDcSelected, getHasAllUsStatesAndTerritoriesSelected } from '../userauth/program/controls/utils';
import { ACCOUNT_MARGIN_TYPE_CASH_TOOLTIP, ACCOUNT_MARGIN_TYPE_LPM_TOOLTIP } from './account/account-tooltips';
import { formatAddress, ONE_LINE_ADDRESS_FORMAT_LAYOUTS } from './shared-utils/address';
import { formatName } from './shared-utils/name';

function AccountTypesNode({ accountTypes }: { accountTypes: IxcRegistrationV1RegistrationAccountType[] }) {
  const { t } = useTranslation('ixcRegistrationV1');
  const mappedNames = accountTypes?.map((accountType) => t(`ixcRegistrationV1:enum.IxcRegistrationV1RegistrationAccountType.${accountType}`));

  if (!mappedNames.length) {
    return null;
  }

  return <span>{mappedNames?.join(', ')}</span>;
}

function StatesNode({ states }: { states: IxcRegistrationV1UsState[] }) {
  const { t } = useTranslation('ixcRegistrationV1');
  const mappedNames = states?.map((state) => t(`ixcRegistrationV1:enum.IxcRegistrationV1UsState.${state}`));

  if (!mappedNames.length) {
    return null;
  }

  return <span>{mappedNames?.join(', ')}</span>;
}

export function getRegistrationClearingControlSettingsListItems(data: IxcRegistrationV1ClearingControlsSettings | undefined, prefix = '') {
  return {
    marginType: getReadonlyListItemTranslatedString(`${prefix}marginType`, 'Margin Type', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1ControlsMarginType',
      value: data?.marginType,
    }),
    canOpenAccounts: getReadonlyListItemYesOrNo(`${prefix}canOpenAccounts`, 'Open Accounts', Boolean(data?.canOpenAccounts)),
    optionalSuitabilityQuestions: getReadonlyListItemYesOrNo(
      `${prefix}optionalSuitabilityQuestions`,
      'Optional Suitability Questions',
      Boolean(data?.optionalSuitabilityQuestions),
    ),
    accountReviewType: getReadonlyListItemTranslatedString(`${prefix}accountReviewType`, 'Account Review Type', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1AccountReviewType',
      value: data?.accountReviewType,
    }),
    specialPurposeAccountReviewType: getReadonlyListItemTranslatedString(
      `${prefix}specialPurposeAccountReviewType`,
      'Special Purpose Account Review Type',
      {
        namespace: 'ixcRegistrationV1',
        enumName: 'IxcRegistrationV1AccountReviewType',
        value: data?.specialPurposeAccountReviewType,
      },
    ),
    cipProgram: getReadonlyListItemTranslatedString(`${prefix}cipProgram`, 'CIP Program', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1ClearingControlsSettingsCipProgram',
      value: data?.cipProgram,
    }),
    accountTypes: getReadonlyListItemNode(
      `${prefix}accountTypes`,
      'Account Types',
      data?.accountTypes ? <AccountTypesNode accountTypes={data?.accountTypes} /> : null,
    ),
    states: getReadonlyListItemNode(`${prefix}states`, 'States', data?.states ? <StatesNode states={data?.states} /> : null),
    statesSummary: getReadonlyListItemString(
      `${prefix}statesSummary`,
      'State & Territory Registrations',
      match(data?.states)
        .with(
          P.when((s) => getHasAllUsStatesAndDcSelected(s)),
          () => `50 U.S. States + D.C.`,
        )
        .with(
          P.when((s) => getHasAllUsStatesAndTerritoriesSelected(s)),
          (s) => `${s?.length} U.S. States & Territories`,
        )
        .with(
          P.when((s) => s && s.length > 0),
          (s) => s?.map((s) => s.toUpperCase()).join(', '),
        )
        .otherwise(() => undefined),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingControlsListItems(data: IxcRegistrationV1ClearingControls | undefined, prefix = '') {
  return {
    settings: getRegistrationClearingControlSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramInstantDepositControlsSettingsListItems(
  data: IxcRegistrationV1ProgramControlsInstantDepositSettings | undefined,
  prefix = '',
) {
  return {
    canUseAdvance: getReadonlyListItemYesOrNo(`${prefix}canUseAdvance`, 'Allow Advances', Boolean(data?.canUseAdvance)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramInstantDepositControlsLimitsListItems(
  data: IxcRegistrationV1ProgramControlsInstantDepositLimits | undefined,
  prefix = '',
) {
  return {
    maxAdvance: getReadonlyListItemCurrency(`${prefix}maxAdvance`, 'Advance Max', data?.maxAdvance),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramInstantDepositControlsListItems(data: IxcRegistrationV1ProgramControlsInstantDeposit | undefined, prefix = '') {
  return {
    settings: getRegistrationProgramInstantDepositControlsSettingsListItems(data?.settings, `${prefix}settings.`),
    limits: getRegistrationProgramInstantDepositControlsLimitsListItems(data?.limits, `${prefix}limits.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingAmountLimitsListItems(data: IxcRegistrationV1AccountFundingAmountLimits | undefined, prefix = '') {
  return {
    minAmount: getReadonlyListItemCurrency(`${prefix}minAmount`, 'Min Amount', data?.minAmount),
    maxAmount: getReadonlyListItemCurrency(`${prefix}maxAmount`, 'Max Amount', data?.maxAmount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsAchLimitsListItems(
  data: IxcRegistrationV1ProgramControlsFundingLimitsAch | undefined,
  prefix = '',
) {
  return {
    depositPerTransaction: getRegistrationProgramFundingAmountLimitsListItems(data?.depositPerTransaction, `${prefix}depositPerTransaction.`),
    withdrawalPerTransaction: getRegistrationProgramFundingAmountLimitsListItems(
      data?.withdrawalPerTransaction,
      `${prefix}withdrawalPerTransaction.`,
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsWireLimitsListItems(
  data: IxcRegistrationV1ProgramControlsFundingLimitsWire | undefined,
  prefix = '',
) {
  return {
    withdrawalPerTransaction: getRegistrationProgramFundingAmountLimitsListItems(
      data?.withdrawalPerTransaction,
      `${prefix}withdrawalPerTransaction.`,
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsSameDayAchLimitsListItems(
  data: IxcRegistrationV1ProgramControlsFundingLimitsSameDayAch | undefined,
  prefix = '',
) {
  return {
    depositPerTransaction: getRegistrationProgramFundingAmountLimitsListItems(data?.depositPerTransaction, `${prefix}depositPerTransaction.`),
    withdrawalPerTransaction: getRegistrationProgramFundingAmountLimitsListItems(
      data?.withdrawalPerTransaction,
      `${prefix}withdrawalPerTransaction.`,
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsLimitsListItems(data: IxcRegistrationV1ProgramControlsFundingLimits | undefined, prefix = '') {
  return {
    maxSources: getReadonlyListItemNumber(`${prefix}maxSources`, 'Max Funding Sources', data?.maxSources),
    ach: getRegistrationProgramFundingControlsAchLimitsListItems(data?.ach, `${prefix}ach.`),
    sameDayAch: getRegistrationProgramFundingControlsSameDayAchLimitsListItems(data?.sameDayAch, `${prefix}sameDayAch.`),
    wire: getRegistrationProgramFundingControlsWireLimitsListItems(data?.wire, `${prefix}wire.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsAchSettingsListItems(
  data: IxcRegistrationV1ProgramControlsFundingSettingsAch | undefined,
  prefix = '',
) {
  return {
    canDeposit: getReadonlyListItemYesOrNo(`${prefix}canDeposit`, 'Pull', Boolean(data?.canDeposit)),
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsSameDayAchSettingsListItems(
  data: IxcRegistrationV1ProgramControlsFundingSettingsSameDayAch | undefined,
  prefix = '',
) {
  return {
    canDeposit: getReadonlyListItemYesOrNo(`${prefix}canDeposit`, 'Pull', Boolean(data?.canDeposit)),
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsWireSettingsListItems(
  data: IxcRegistrationV1ProgramControlsFundingSettingsWire | undefined,
  prefix = '',
) {
  return {
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsSettingsListItems(
  data: IxcRegistrationV1ProgramControlsFundingSettings | undefined,
  prefix = '',
) {
  return {
    ach: getRegistrationProgramFundingControlsAchSettingsListItems(data?.ach, `${prefix}ach.`),
    sameDayAch: getRegistrationProgramFundingControlsSameDayAchSettingsListItems(data?.sameDayAch, `${prefix}sameDayAch.`),
    wire: getRegistrationProgramFundingControlsWireSettingsListItems(data?.wire, `${prefix}wire.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramAccountNumbersSettingsListItems(
  data: IxcRegistrationV1ProgramControlsAccountNumbersSettings | undefined,
  prefix = '',
) {
  return {
    canCreate: getReadonlyListItemYesOrNo(`${prefix}canCreate`, 'Can Create', Boolean(data?.canCreate)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramAccountNumbersListItems(data: IxcRegistrationV1ProgramControlsAccountNumbers | undefined, prefix = '') {
  return {
    settings: getRegistrationProgramAccountNumbersSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramFundingControlsListItems(data: IxcRegistrationV1ProgramControlsFunding | undefined, prefix = '') {
  return {
    limits: getRegistrationProgramFundingControlsLimitsListItems(data?.limits, `${prefix}limits.`),
    settings: getRegistrationProgramFundingControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramSweepsControlsSettingsListItems(data: IxcRegistrationV1ProgramControlsSweepsSettings | undefined, prefix = '') {
  return {
    canSweepAccount: getReadonlyListItemYesOrNo(`${prefix}canSweepAccount`, 'Sweep Account', Boolean(data?.canSweepAccount)),
    sweepPlanId: getReadonlyListItemString(`${prefix}sweepPlanId`, 'Sweep Plan ID', data?.sweepPlanId),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramSweepsControlsListItems(data: IxcRegistrationV1ProgramControlsSweeps | undefined, prefix = '') {
  return {
    settings: getRegistrationProgramSweepsControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramOrderControlsUsEquitySettingsListItems(
  data: IxcRegistrationV1ProgramControlsOrderSettingsUsEquities | undefined,
  prefix = '',
) {
  return {
    canPlaceClosingOrders: getReadonlyListItemYesOrNo(`${prefix}canPlaceClosing`, 'Place Closing Orders', Boolean(data?.canPlaceClosing)),
    canPlaceOpeningOrders: getReadonlyListItemYesOrNo(`${prefix}canPlaceOpening`, 'Place Opening Orders', Boolean(data?.canPlaceOpening)),
    canPlaceFractional: getReadonlyListItemYesOrNo(`${prefix}canPlaceFractional`, 'Trade Fractions', Boolean(data?.canTradeFractions)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramOrderControlsSettingsListItems(data: IxcRegistrationV1ProgramControlsOrderSettings | undefined, prefix = '') {
  return {
    usEquities: getRegistrationProgramOrderControlsUsEquitySettingsListItems(data?.usEquities, `${prefix}usEquities.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramOrderControlsListItems(data: IxcRegistrationV1ProgramControlsOrder | undefined, prefix = '') {
  return {
    settings: getRegistrationProgramOrderControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramBatchOrderControlsUsEquitySettingsListItems(
  data: IxcRegistrationV1ProgramControlsBatchOrderSettingsUsEquities | undefined,
  prefix = '',
) {
  return {
    canPlaceBatchOrders: getReadonlyListItemYesOrNo(`${prefix}canPlaceBatchOrders`, 'Place Batch Orders', Boolean(data?.canPlaceBatchOrders)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramBatchOrderControlsSettingsListItems(
  data: IxcRegistrationV1ProgramControlsBatchOrderSettings | undefined,
  prefix = '',
) {
  return {
    usEquities: getRegistrationProgramBatchOrderControlsUsEquitySettingsListItems(data?.usEquities, `${prefix}usEquities.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramBatchOrderControlsListItems(data: IxcRegistrationV1ProgramControlsBatchOrder | undefined, prefix = '') {
  return {
    settings: getRegistrationProgramBatchOrderControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramRegistrationControlsSettingsListItems(
  data: IxcRegistrationV1ProgramControlsRegistrationSettings | undefined,
  prefix = '',
) {
  const marginTypeTooltip = match(data?.marginType)
    .with(IxcRegistrationV1ControlsMarginType.Cash, () => ACCOUNT_MARGIN_TYPE_CASH_TOOLTIP)
    .with(IxcRegistrationV1ControlsMarginType.Lpm, () => ACCOUNT_MARGIN_TYPE_LPM_TOOLTIP)
    .otherwise(() => undefined);

  return {
    marginType: getReadonlyListItemTranslatedString(
      `${prefix}marginType`,
      'Margin Type',
      {
        namespace: 'ixcRegistrationV1',
        enumName: 'IxcRegistrationV1ControlsMarginType',
        value: data?.marginType,
      },
      {
        action: marginTypeTooltip
          ? buildTooltipInteraction(
              marginTypeTooltip,
              <PlainIconButton aria-label="More information">
                <InfoIcon aria-hidden width={16} />
              </PlainIconButton>,
              { maxWidth: 200 },
            )
          : undefined,
      },
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramRegistrationControlsListItems(data: IxcRegistrationV1ProgramControlsRegistration | undefined, prefix = '') {
  return {
    settings: getRegistrationProgramRegistrationControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProgramControlsListItems(data: IxcRegistrationV1ProgramControls | undefined, prefix = '') {
  return {
    order: getRegistrationProgramOrderControlsListItems(data?.order, `${prefix}order.`),
    sweeps: getRegistrationProgramSweepsControlsListItems(data?.sweeps, `${prefix}sweeps.`),
    funding: getRegistrationProgramFundingControlsListItems(data?.funding, `${prefix}funding.`),
    registration: getRegistrationProgramRegistrationControlsListItems(data?.registration, `${prefix}registration.`),
    instantDeposit: getRegistrationProgramInstantDepositControlsListItems(data?.instantDeposit, `${prefix}instantDeposit.`),
    batchOrder: getRegistrationProgramBatchOrderControlsListItems(data?.batchOrder, `${prefix}batchOrder.`),
    accountNumbers: getRegistrationProgramAccountNumbersListItems(data?.accountNumbers, `${prefix}accountNumbers.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountOrderControlsListItems(data: IxcRegistrationV1ClearingAccountControlsOrder | undefined, prefix = '') {
  return {
    canPlaceClosingOrders: getReadonlyListItemYesOrNo(`${prefix}canPlaceClosing`, 'Place Closing Orders', Boolean(data?.canPlaceClosing)),
    canPlaceOpeningOrders: getReadonlyListItemYesOrNo(`${prefix}canPlaceOpening`, 'Place Opening Orders', Boolean(data?.canPlaceOpening)),
    canPlaceFractional: getReadonlyListItemYesOrNo(`${prefix}canPlaceFractional`, 'Trade Fractions', Boolean(data?.canTradeFractions)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountBatchOrderControlsListItems(
  data: IxcRegistrationV1ClearingAccountControlsBatchOrder | undefined,
  prefix = '',
) {
  return {
    canPlaceBatchOrders: getReadonlyListItemYesOrNo(`${prefix}canPlaceBatchOrders`, 'Place Batch Orders', Boolean(data?.canPlaceBatchOrders)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountSweepsControlsListItems(data: IxcRegistrationV1ClearingAccountControlsSweeps | undefined, prefix = '') {
  return {
    canSweepAccount: getReadonlyListItemYesOrNo(`${prefix}canSweepAccount`, 'Sweep Account', Boolean(data?.canSweepAccount)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountFundingControlsAchListItems(
  data: IxcRegistrationV1ProgramControlsFundingSettingsAch | undefined,
  prefix = '',
) {
  return {
    canDeposit: getReadonlyListItemYesOrNo(`${prefix}canDeposit`, 'Pull', Boolean(data?.canDeposit)),
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountFundingControlsSameDayAchListItems(
  data: IxcRegistrationV1ProgramControlsFundingSettingsSameDayAch | undefined,
  prefix = '',
) {
  return {
    canDeposit: getReadonlyListItemYesOrNo(`${prefix}canDeposit`, 'Pull', Boolean(data?.canDeposit)),
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountFundingControlsWireListItems(
  data: IxcRegistrationV1ProgramControlsFundingSettingsWire | undefined,
  prefix = '',
) {
  return {
    canWithdraw: getReadonlyListItemYesOrNo(`${prefix}canWithdraw`, 'Push', Boolean(data?.canWithdraw)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountFundingControlsListItems(
  data: IxcRegistrationV1ClearingAccountControlsFunding | undefined,
  prefix = '',
) {
  return {
    ach: getRegistrationClearingAccountFundingControlsAchListItems(data?.ach, `${prefix}ach.`),
    sameDayAch: getRegistrationClearingAccountFundingControlsSameDayAchListItems(data?.sameDayAch, `${prefix}sameDayAch.`),
    wire: getRegistrationClearingAccountFundingControlsWireListItems(data?.wire, `${prefix}wire.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountAccountNumbersSettingsListItems(
  data: IxcRegistrationV1ClearingAccountControlsAccountNumbersSettings | undefined,
  prefix = '',
) {
  return {
    canCreate: getReadonlyListItemYesOrNo(`${prefix}canCreate`, 'Can Create', Boolean(data?.canCreate)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountAccountNumbersListItems(
  data: IxcRegistrationV1ClearingAccountControlsAccountNumbers | undefined,
  prefix = '',
) {
  return {
    settings: getRegistrationClearingAccountAccountNumbersSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationClearingAccountControlsListItems(data: IxcRegistrationV1ClearingAccountControls | undefined, prefix = '') {
  return {
    order: getRegistrationClearingAccountOrderControlsListItems(data?.order, `${prefix}order.`),
    sweeps: getRegistrationClearingAccountSweepsControlsListItems(data?.sweeps, `${prefix}sweeps.`),
    funding: getRegistrationClearingAccountFundingControlsListItems(data?.funding, `${prefix}funding.`),
    batchOrder: getRegistrationClearingAccountBatchOrderControlsListItems(data?.batchOrder, `${prefix}batchOrder.`),
    accountNumbers: getRegistrationClearingAccountAccountNumbersListItems(data?.accountNumbers, `${prefix}accountNumbers.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationPlatformCreditControlsSettingsListItems(
  data: IxcRegistrationV1PlatformControlsCreditSettings | undefined,
  prefix = '',
) {
  return {
    skipCreditCheck: getReadonlyListItemYesOrNo(`${prefix}skipCreditCheck`, 'Skip Credit Check', Boolean(data?.skipCreditCheck)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationPlatformCreditControlsListItems(data: IxcRegistrationV1PlatformControlsCredit | undefined, prefix = '') {
  return {
    settings: getRegistrationPlatformCreditControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationPlatformControlsListItems(data: IxcRegistrationV1PlatformControls | undefined, prefix = '') {
  return {
    credit: getRegistrationPlatformCreditControlsListItems(data?.credit, `${prefix}credit.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationProfileSummaryDataListItems(data: IxcRegistrationV1ProfileSummaryData | undefined, prefix = '') {
  const typeNameColumn = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcRegistrationV1',
      oneOfName: 'IxcRegistrationV1ProfileSummaryData',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameColumn;
  }

  if ('individual' in data) {
    return {
      ...typeNameColumn,
      individual: {
        '!type': 'individual',
        value: {
          name: getRegistrationNameListItems(data.individual.name, `${prefix}individual.name`),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('organization' in data) {
    return {
      ...typeNameColumn,
      organization: {
        '!type': 'organization',
        value: {
          legalName: getReadonlyListItemString(`${prefix}organization.legalName`, 'Legal Name', data.organization.legalName),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameColumn;
}

export function getRegistrationOrganizationTaxIdentifierReadonlyListItem(
  data: IxcRegistrationV1OrganizationTaxIdentifier | undefined,
  prefix: string = '',
) {
  return getReadonlyListItemSensitiveString(
    prefix,
    match(data)
      .with({ ein: P.not(P.nullish) }, () => 'EIN')
      .otherwise(() => 'Social Security Number'),
    match(data)
      .with({ ssn: P.not(P.nullish) }, ({ ssn }) => ssn.number)
      .with({ ein: P.not(P.nullish) }, ({ ein }) => ein.number)
      .otherwise(() => undefined),
    match(data)
      .with({ ssn: P.not(P.nullish) }, () => maskSSN)
      .with({ ein: P.not(P.nullish) }, () => maskEIN)
      .otherwise(() => undefined),
  );
}

export function getRegistrationIndividualTaxIdentifierReadonlyListItem(
  data: IxcRegistrationV1IndividualTaxIdentifier | undefined,
  prefix: string = '',
) {
  return getReadonlyListItemSensitiveString(
    prefix,
    match(data)
      .with({ itin: P.not(P.nullish) }, () => 'ITIN')
      .otherwise(() => 'Social Security Number'),
    match(data)
      .with({ ssn: P.not(P.nullish) }, ({ ssn }) => ssn.number)
      .with({ itin: P.not(P.nullish) }, ({ itin }) => itin.number)
      .otherwise(() => undefined),
    match(data)
      .with({ ssn: P.not(P.nullish) }, () => maskSSN)
      .with({ itin: P.not(P.nullish) }, () => maskITIN)
      .otherwise(() => undefined),
  );
}

export function getRegistrationNameListItems(data: IxcRegistrationV1Name | undefined, prefix: string) {
  return {
    prefix: getReadonlyListItemString(`${prefix}.prefix`, 'Prefix', data?.prefix),
    first: getReadonlyListItemString(`${prefix}.first`, 'First Name', data?.first),
    last: getReadonlyListItemString(`${prefix}.last`, 'Last Name', data?.last),
    middle: getReadonlyListItemString(`${prefix}.middle`, 'Middle Name', data?.middle),
    suffix: getReadonlyListItemString(`${prefix}.suffix`, 'Suffix', data?.suffix),
    preferred: getReadonlyListItemString(`${prefix}.preferred`, 'Preferred Name', data?.preferred),
    formatted: getReadonlyListItemString(`${prefix}.formatted`, 'Name', formatName(data)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationUsAddressListItems(data: IxcRegistrationV1AddressUsAddress | undefined, prefix: string) {
  return {
    street1: getReadonlyListItemString(`${prefix}.street1`, 'Street 1', data?.street1),
    street2: getReadonlyListItemString(`${prefix}.street2`, 'Street 2', data?.street2),
    city: getReadonlyListItemString(`${prefix}.city`, 'City', data?.city),
    state: getReadonlyListItemTranslatedString(`${prefix}.state`, 'State', {
      namespace: 'ixcRegistrationV1',
      enumName: 'IxcRegistrationV1UsState',
      value: data?.state,
    }),
    postalCode: getReadonlyListItemString(`${prefix}.postalCode`, 'ZIP Code', data?.postalCode),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRegistrationAddressListItems(data: IxcRegistrationV1Address | undefined, prefix: string, title: string = 'Address') {
  const formattedAddressColumn = {
    formatted: getReadonlyListItemString(`${prefix}.formatted`, title, formatAddress(data, ONE_LINE_ADDRESS_FORMAT_LAYOUTS)),
    formattedMultiLine: getReadonlyListItemString(`${prefix}.formattedMultiLine`, title, formatAddress(data)),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return formattedAddressColumn;
  }

  if ('usAddress' in data) {
    return {
      ...formattedAddressColumn,
      usAddress: getRegistrationUsAddressListItems(data.usAddress, `${prefix}.usAddress`),
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return formattedAddressColumn;
}
